import {
  isAccessTokenExpired,
  isRefreshTokenExpired,
  calculateNextRefreshTime,
  isAuthenticatedUser
} from '../session/session.js';
import { refreshTokenV2 } from './ejm-api.js';

/**
 * Manages proactive token refresh for single-tab operation
 * Optimized for PWA usage
 */
class TokenRefreshWatcher {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    
    // Bind event handlers once
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleAppFocus = this.handleAppFocus.bind(this);
  }

  start() {
    console.log('[TokenWatcher] Starting token refresh watcher');
    
    if (!isAuthenticatedUser()) {
      console.log('[TokenWatcher] User not authenticated, skipping');
      return;
    }
    
    this.stop();
    this.scheduleNextRefresh();
    
    // Listen for online/offline events (critical for PWA)
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Listen for visibility changes (critical for PWA)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Listen for app focus (iOS PWA specific)
    window.addEventListener('focus', this.handleAppFocus);
  }

  stop() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    // Remove event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleAppFocus);
  }

  scheduleNextRefresh() {
    const nextRefreshTime = calculateNextRefreshTime();
    
    if (nextRefreshTime === 0) {
      // Need immediate refresh
      this.performRefresh();
    } else {
      console.log(`[TokenWatcher] Next refresh in ${Math.round(nextRefreshTime / 1000)}s`);
      this.refreshTimer = setTimeout(() => {
        this.performRefresh();
      }, nextRefreshTime);
    }
  }

  async performRefresh() {
    if (this.isRefreshing) {
      console.log('[TokenWatcher] Already refreshing, skipping');
      return;
    }
    
    this.isRefreshing = true;
    
    try {
      console.log('[TokenWatcher] Performing token refresh');
      const result = await refreshTokenV2();
      
      if (result && result.expiry) {
        console.log('[TokenWatcher] Token refresh successful');
      }
    } catch (error) {
      console.error('[TokenWatcher] Token refresh failed:', error);
      
      // Only fire auth:expired if user WAS authenticated and refresh token is now expired
      // This prevents redirecting users who were never logged in (e.g., on public routes)
      if (isAuthenticatedUser() && isRefreshTokenExpired()) {
        console.log('[TokenWatcher] Authenticated user refresh token expired, firing auth:expired event');
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }
    } finally {
      this.isRefreshing = false;
      this.scheduleNextRefresh();
    }
  }

  handleOnline() {
    console.log('[TokenWatcher] Back online, checking token status');
    
    if (isAccessTokenExpired()) {
      this.performRefresh();
    } else {
      this.scheduleNextRefresh();
    }
  }

  handleOffline() {
    console.log('[TokenWatcher] Going offline, pausing refresh timer');
    this.stop();
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      console.log('[TokenWatcher] App visible, checking token status');
      
      if (!this.refreshTimer) {
        this.start();
      } else if (isAccessTokenExpired()) {
        this.stop();
        this.performRefresh();
      }
    }
  }

  handleAppFocus() {
    console.log('[TokenWatcher] App focused, ensuring fresh tokens');
    
    if (isAccessTokenExpired()) {
      this.performRefresh();
    }
  }
}

// Singleton instance
let watcherInstance = null;

/**
 * Get the singleton TokenRefreshWatcher instance
 * @returns {TokenRefreshWatcher}
 */
export function getTokenRefreshWatcher() {
  if (!watcherInstance) {
    watcherInstance = new TokenRefreshWatcher();
  }
  return watcherInstance;
}

export default TokenRefreshWatcher;